import React, { useState, useMemo, useEffect } from "react";
import { useNavigationEvent } from "navigation-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faReceipt,
  faSpinner,
  faCheckCircle,
  faClose,
  faTimes,
  faUtensils,
  faBagShopping,
  faMotorcycle,
  faMoneyBillWave,
  faRefresh,
  faPrint,
  faSearch,
  faCalendar,
  faFileInvoice,
  faChartLine,
  faCashRegister,
  faUsers,
  faMoneyBillTransfer,
  faUser,
  faFilter,
  faMobile,
  faCreditCard,
  faLandmark,
  faPercent,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import useSWR from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";
import SecureLS from "secure-ls";
import UnifiedRefundModal from "./UnifiedRefundModal";

const ls = new SecureLS({ encodingType: "aes" });

// Helper to fetch CSRF token
const fetchCsrfToken = async () => {
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/session/token`,
    {
      method: "GET",
      credentials: "include",
    },
  );
  if (!response.ok) {
    throw new Error("Failed to fetch CSRF token");
  }
  return await response.text();
};

// Fetcher for orders API
const ordersFetcher = async (url) => {
  try {
    const response = await fetch(url, {
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

// Fetch bill details for an order
const fetchBillDetails = async (orderId) => {
  try {
    const csrfToken = await fetchCsrfToken();
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/api/order/${orderId}/bill`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
      },
    );

    if (response.ok) {
      const data = await response.json();
      return data.bill;
    }
    return null;
  } catch (error) {
    console.error("Error fetching bill details:", error);
    return null;
  }
};

// Format date for API
const formatDateForAPI = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function CashRegister() {
  const { stateNavigator } = useNavigationEvent();

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [toDate, setToDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [selectedCashier, setSelectedCashier] = useState("all");
  const [selectedRefundedBy, setSelectedRefundedBy] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [selectedRefundOrder, setSelectedRefundOrder] = useState(null);
  const [billDetails, setBillDetails] = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);

  // Build API URL with date range
  const getApiUrl = () => {
    const fromDateParam = formatDateForAPI(fromDate);
    const toDateParam = formatDateForAPI(toDate);
    return `${process.env.REACT_APP_BASE_URL}/api/orders?from_date=${fromDateParam}&to_date=${toDateParam}`;
  };

  // Fetch orders
  const {
    data: ordersResponse,
    isLoading,
    error,
    mutate,
  } = useSWR(getApiUrl(), ordersFetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    refreshInterval: 30000,
  });

  const orders = ordersResponse?.data || [];

  // Filter orders - ONLY show orders with status "paid"
  const paidOrders = orders.filter((order) => order.status === "paid");

  // Get unique cashiers from paid orders
  const cashierOptions = useMemo(() => {
    const cashiers = new Set();
    paidOrders.forEach((order) => {
      const cashierName = order.cashier_name || order.cashier;
      if (cashierName && cashierName !== "N/A") {
        cashiers.add(cashierName);
      }
    });
    return Array.from(cashiers).sort();
  }, [paidOrders]);

  // Get unique refunded by from paid orders
  const refundedByOptions = useMemo(() => {
    const refundedBy = new Set();
    paidOrders.forEach((order) => {
      if (
        order.refunded_by &&
        order.refunded_by !== "N/A" &&
        order.refunded_by !== ""
      ) {
        refundedBy.add(order.refunded_by);
      }
    });
    console.log("Refunded By Options:", Array.from(refundedBy));
    return Array.from(refundedBy).sort();
  }, [paidOrders]);

  // Filter orders by selected cashier for statistics
  const filteredOrdersForStats = useMemo(() => {
    if (!paidOrders.length) return [];

    let filtered = [...paidOrders];

    // Filter by cashier
    if (selectedCashier !== "all") {
      filtered = filtered.filter((order) => {
        const cashierName = order.cashier_name || order.cashier || "N/A";
        return cashierName === selectedCashier;
      });
    }

    // Filter by refunded by
    if (selectedRefundedBy !== "all") {
      filtered = filtered.filter((order) => {
        const refundedByName = order.refunded_by || "";
        return refundedByName === selectedRefundedBy;
      });
    }

    return filtered;
  }, [paidOrders, selectedCashier, selectedRefundedBy]);

  // Calculate current subtotal (with discount and refunds applied)
  const calculateCurrentSubtotal = (order) => {
    if (!order.items || !order.items.length) return 0;

    let subtotal = 0;
    order.items.forEach((item) => {
      const remainingQty = item.quantity - (item.refunded_quantity || 0);
      subtotal += item.price * remainingQty;
    });

    const discount = order.discount || 0;
    return Math.max(0, subtotal - discount);
  };

  // Calculate current service charge (based on discounted subtotal)
  const calculateCurrentServiceCharge = (order) => {
    const subtotal = calculateCurrentSubtotal(order);
    const scPercentage =
      order.service_charge_percentage ||
      (order.order_type === "dine-in" ? 5 : 0);
    return subtotal * (scPercentage / 100);
  };

  // Calculate food refund amount
  const calculateFoodRefundAmount = (order) => {
    if (!order.items || !order.items.length) return 0;

    let foodRefundedAmount = 0;
    order.items.forEach((item) => {
      const refundedQty = item.refunded_quantity || 0;
      if (refundedQty > 0) {
        foodRefundedAmount += item.price * refundedQty;
      }
    });

    return foodRefundedAmount;
  };

  // Calculate total refunded amount INCLUDING service charge for dine-in
  const calculateTotalRefunded = (orders) => {
    let total = 0;
    orders.forEach((order) => {
      const foodRefund = calculateFoodRefundAmount(order);

      let serviceChargeRefund = 0;
      if (order.order_type === "dine-in") {
        const scPercentage = order.service_charge_percentage || 5;
        serviceChargeRefund = foodRefund * (scPercentage / 100);
      }

      total += foodRefund + serviceChargeRefund;
    });
    return total;
  };

  // Get payment type breakdown
  const getPaymentTypeBreakdown = (orders) => {
    const breakdown = {
      cash: {
        count: 0,
        amount: 0,
        label: "Cash",
        icon: faMoneyBillWave,
        color: "#4CAF50",
      },
      card: {
        count: 0,
        amount: 0,
        label: "Card",
        icon: faCreditCard,
        color: "#2196F3",
      },
      mobile_money: {
        count: 0,
        amount: 0,
        label: "Mobile Money",
        icon: faMobile,
        color: "#9C27B0",
      },
      mobilemoney: {
        count: 0,
        amount: 0,
        label: "Mobile Money",
        icon: faMobile,
        color: "#9C27B0",
      },
      momo: {
        count: 0,
        amount: 0,
        label: "Mobile Money",
        icon: faMobile,
        color: "#9C27B0",
      },
      bank_transfer: {
        count: 0,
        amount: 0,
        label: "Bank Transfer",
        icon: faLandmark,
        color: "#FF9800",
      },
      banktransfer: {
        count: 0,
        amount: 0,
        label: "Bank Transfer",
        icon: faLandmark,
        color: "#FF9800",
      },
      other: {
        count: 0,
        amount: 0,
        label: "Other",
        icon: faMoneyBillWave,
        color: "#9E9E9E",
      },
    };

    orders.forEach((order) => {
      const method = order.payment_method?.toLowerCase() || "other";
      const amount = order.total || 0;

      if (breakdown[method]) {
        breakdown[method].count++;
        breakdown[method].amount += amount;
      } else {
        breakdown.other.count++;
        breakdown.other.amount += amount;
      }
    });

    // Remove empty entries and merge mobile money variants
    const result = {};
    const mobileMoneyTotal = {
      count: 0,
      amount: 0,
      label: "Mobile Money",
      icon: faMobile,
      color: "#9C27B0",
    };

    // Collect mobile money variants
    ["mobile_money", "mobilemoney", "momo"].forEach((key) => {
      if (breakdown[key] && breakdown[key].count > 0) {
        mobileMoneyTotal.count += breakdown[key].count;
        mobileMoneyTotal.amount += breakdown[key].amount;
        delete breakdown[key];
      }
    });

    // Add mobile money if it has data
    if (mobileMoneyTotal.count > 0) {
      result.mobile_money = mobileMoneyTotal;
    }

    // Add other payment methods
    ["cash", "card", "bank_transfer", "other"].forEach((key) => {
      if (breakdown[key] && breakdown[key].count > 0) {
        result[key] = breakdown[key];
      }
    });

    return result;
  };

  // Calculate statistics with cashier filtering
  const statistics = useMemo(() => {
    const ordersToUse = filteredOrdersForStats;

    if (!ordersToUse.length) {
      return {
        totalSales: 0,
        totalRevenue: 0,
        totalOrders: 0,
        totalServiceCharge: 0,
        dineInOrders: 0,
        takeawayOrders: 0,
        deliveryOrders: 0,
        totalGuests: 0,
        totalRefunded: 0,
        totalDiscount: 0,
        paymentBreakdown: {},
        cashPayments: 0,
        cardPayments: 0,
        mobileMoneyPayments: 0,
        bankTransferPayments: 0,
        selectedCashier: selectedCashier,
        selectedRefundedBy: selectedRefundedBy,
      };
    }

    let totalSales = 0;
    let totalServiceCharge = 0;
    let totalRevenue = 0;
    let totalDiscount = 0;
    let dineInOrders = 0;
    let takeawayOrders = 0;
    let deliveryOrders = 0;
    let totalGuests = 0;

    ordersToUse.forEach((order) => {
      const subtotal = calculateCurrentSubtotal(order);
      const serviceCharge = calculateCurrentServiceCharge(order);
      const orderTotal = subtotal + serviceCharge;

      totalSales += subtotal;
      totalServiceCharge += serviceCharge;
      totalRevenue += orderTotal;
      totalDiscount += order.discount || 0;

      switch (order.order_type) {
        case "dine-in":
          dineInOrders++;
          if (order.number_of_people) {
            totalGuests += order.number_of_people;
          }
          break;
        case "takeaway":
          takeawayOrders++;
          break;
        case "delivery":
          deliveryOrders++;
          break;
        default:
          break;
      }
    });

    const totalRefunded = calculateTotalRefunded(ordersToUse);
    const paymentBreakdown = getPaymentTypeBreakdown(ordersToUse);

    // Extract payment type totals
    const cashPayments = paymentBreakdown.cash?.amount || 0;
    const cardPayments = paymentBreakdown.card?.amount || 0;
    const mobileMoneyPayments = paymentBreakdown.mobile_money?.amount || 0;
    const bankTransferPayments = paymentBreakdown.bank_transfer?.amount || 0;

    return {
      totalSales,
      totalRevenue,
      totalOrders: ordersToUse.length,
      totalServiceCharge,
      dineInOrders,
      takeawayOrders,
      deliveryOrders,
      totalGuests,
      totalRefunded,
      totalDiscount,
      paymentBreakdown,
      cashPayments,
      cardPayments,
      mobileMoneyPayments,
      bankTransferPayments,
      selectedCashier: selectedCashier,
      selectedRefundedBy: selectedRefundedBy,
    };
  }, [filteredOrdersForStats, selectedCashier, selectedRefundedBy]);

  // Get order type info
  const getOrderTypeInfo = (type) => {
    switch (type) {
      case "dine-in":
        return {
          icon: faUtensils,
          label: "Dine In",
          color: "#FF6B35",
          bgColor: "#FEF3F2",
        };
      case "takeaway":
        return {
          icon: faBagShopping,
          label: "Takeaway",
          color: "#10B981",
          bgColor: "#ECFDF5",
        };
      case "delivery":
        return {
          icon: faMotorcycle,
          label: "Delivery",
          color: "#3B82F6",
          bgColor: "#EFF6FF",
        };
      default:
        return {
          icon: faUtensils,
          label: "Dine In",
          color: "#FF6B35",
          bgColor: "#FEF3F2",
        };
    }
  };

  // Get payment type icon and label
  const getPaymentTypeInfo = (paymentMethod) => {
    const methods = {
      cash: { icon: faMoneyBillWave, label: "Cash", color: "#4CAF50" },
      card: { icon: faCreditCard, label: "Card", color: "#2196F3" },
      mobile_money: { icon: faMobile, label: "Mobile Money", color: "#9C27B0" },
      mobilemoney: { icon: faMobile, label: "Mobile Money", color: "#9C27B0" },
      momo: { icon: faMobile, label: "Mobile Money", color: "#9C27B0" },
      bank_transfer: {
        icon: faLandmark,
        label: "Bank Transfer",
        color: "#FF9800",
      },
      banktransfer: {
        icon: faLandmark,
        label: "Bank Transfer",
        color: "#FF9800",
      },
    };
    return (
      methods[paymentMethod?.toLowerCase()] || {
        icon: faMoneyBillWave,
        label: "Cash",
        color: "#4CAF50",
      }
    );
  };

  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDisplayDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return `GH₵ ${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  // Filter orders by search term, cashier, and refunded by
  const filteredOrders = useMemo(() => {
    if (!paidOrders.length) return [];

    let filtered = [...paidOrders];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (order) =>
          (order.guest_name &&
            order.guest_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          order.id.toString().includes(searchTerm),
      );
    }

    // Filter by cashier
    if (selectedCashier !== "all") {
      filtered = filtered.filter((order) => {
        const cashierName = order.cashier_name || order.cashier || "N/A";
        return cashierName === selectedCashier;
      });
    }

    // Filter by refunded by
    if (selectedRefundedBy !== "all") {
      filtered = filtered.filter((order) => {
        const refundedByName = order.refunded_by || "";
        return refundedByName === selectedRefundedBy;
      });
    }

    return filtered;
  }, [paidOrders, searchTerm, selectedCashier, selectedRefundedBy]);

  // Pagination for filtered orders
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const filteredTotalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCashier, selectedRefundedBy, fromDate, toDate]);

  // Handle view receipt
  const handleViewReceipt = async (order) => {
    if (!order || !order.id) return;
    setSelectedReceipt(order);
    setLoadingReceipt(true);
    setShowReceiptModal(true);

    try {
      const details = await fetchBillDetails(order.id);
      setBillDetails(details);
    } catch (error) {
      console.error("Error fetching receipt:", error);
      setBillDetails(null);
    } finally {
      setLoadingReceipt(false);
    }
  };

  const handleRefund = (order) => {
    if (!order || !order.id) return;
    setSelectedRefundOrder(order);
    setShowRefundModal(true);
  };

  // Handle print receipt
  const handlePrintReceipt = () => {
    const printContent = document.getElementById("receipt-content");
    if (printContent) {
      const originalContents = document.body.innerHTML;
      document.body.innerHTML = printContent.innerHTML;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
    }
  };

  const handleRefresh = () => {
    mutate();
    setCurrentPage(1);
  };

  const handleFromDateChange = (e) => {
    const newDate = new Date(e.target.value);
    newDate.setHours(0, 0, 0, 0);
    setFromDate(newDate);
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    const newDate = new Date(e.target.value);
    newDate.setHours(23, 59, 59, 999);
    setToDate(newDate);
    setCurrentPage(1);
  };

  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < filteredTotalPages;

  const goToPreviousPage = () => {
    if (canGoPrevious) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (canGoNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (isLoading && !ordersResponse) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.loadingContainer}>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                style={{ fontSize: "48px", color: "#FF6B35" }}
              />
              <p>Loading cash register...</p>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  if (error && !ordersResponse) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.errorContainer}>
              <FontAwesomeIcon
                icon={faTimes}
                style={{ fontSize: "48px", color: "#d32f2f" }}
              />
              <h3>Error Loading Data</h3>
              <p>Unable to connect to the API. Please check your connection.</p>
              <button style={styles.retryButton} onClick={handleRefresh}>
                <FontAwesomeIcon
                  icon={faRefresh}
                  style={{ marginRight: "8px" }}
                />
                Retry
              </button>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div style={styles.container}>
          <TopActionMenus
            AddTitle="Cash Register"
            buttonLabel="Void Register"
            onClick={() => stateNavigator.navigate("voidregister")}
          />

          {/* STATISTICS CARDS - Row 1: 5 in a row */}
          <div style={styles.statisticsGrid}>
            {/* Card 1: Total Sales */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E8F5E9",
                }}
              >
                <FontAwesomeIcon
                  icon={faMoneyBillWave}
                  style={{ color: "#4CAF50", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {formatCurrency(statistics.totalSales)}
                </h3>
                <p style={styles.statLabel}>Total Sales</p>
              </div>
            </div>

            {/* Card 2: Service Charge */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FFF3E0",
                }}
              >
                <FontAwesomeIcon
                  icon={faPercent}
                  style={{ color: "#E65100", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {formatCurrency(statistics.totalServiceCharge)}
                </h3>
                <p style={styles.statLabel}>Service Charge</p>
              </div>
            </div>

            {/* Card 3: Total Revenue */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E3F2FD",
                }}
              >
                <FontAwesomeIcon
                  icon={faChartLine}
                  style={{ color: "#2196F3", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {formatCurrency(statistics.totalRevenue)}
                </h3>
                <p style={styles.statLabel}>Total Revenue</p>
              </div>
            </div>

            {/* Card 4: Paid Orders */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E8F5E9",
                }}
              >
                <FontAwesomeIcon
                  icon={faReceipt}
                  style={{ color: "#4CAF50", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.totalOrders}</h3>
                <p style={styles.statLabel}>Paid Orders</p>
              </div>
            </div>

            {/* Card 5: Total Guests */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FCE4EC",
                }}
              >
                <FontAwesomeIcon
                  icon={faUsers}
                  style={{ color: "#E91E63", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {formatNumber(statistics.totalGuests)}
                </h3>
                <p style={styles.statLabel}>Total Guests</p>
              </div>
            </div>
          </div>

          {/* STATISTICS CARDS - Row 2: 5 in a row */}
          <div style={styles.statisticsGrid}>
            {/* Card 6: Total Refunded */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FEE2E2",
                }}
              >
                <FontAwesomeIcon
                  icon={faMoneyBillTransfer}
                  style={{ color: "#DC2626", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={{ ...styles.statValue, color: "#DC2626" }}>
                  {formatCurrency(statistics.totalRefunded)}
                </h3>
                <p style={styles.statLabel}>Total Refunded</p>
              </div>
            </div>

            {/* Card 7: Total Discount */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FFF3E0",
                }}
              >
                <FontAwesomeIcon
                  icon={faPercent}
                  style={{ color: "#E65100", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {formatCurrency(statistics.totalDiscount)}
                </h3>
                <p style={styles.statLabel}>Total Discount</p>
              </div>
            </div>

            {/* Card 8: Cash Payments */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E8F5E9",
                }}
              >
                <FontAwesomeIcon
                  icon={faMoneyBillWave}
                  style={{ color: "#4CAF50", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {formatCurrency(statistics.cashPayments)}
                </h3>
                <p style={styles.statLabel}>Cash Payments</p>
              </div>
            </div>

            {/* Card 9: Card Payments */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E3F2FD",
                }}
              >
                <FontAwesomeIcon
                  icon={faCreditCard}
                  style={{ color: "#2196F3", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {formatCurrency(statistics.cardPayments)}
                </h3>
                <p style={styles.statLabel}>Card Payments</p>
              </div>
            </div>

            {/* Card 10: Mobile Money Payments */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#F3E5F5",
                }}
              >
                <FontAwesomeIcon
                  icon={faMobile}
                  style={{ color: "#9C27B0", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {formatCurrency(statistics.mobileMoneyPayments)}
                </h3>
                <p style={styles.statLabel}>Mobile Money</p>
              </div>
            </div>
          </div>

          {/* STATISTICS CARDS - Row 3: 5 in a row */}
          <div style={styles.statisticsGrid}>
            {/* Card 11: Bank Transfer Payments */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FFF3E0",
                }}
              >
                <FontAwesomeIcon
                  icon={faLandmark}
                  style={{ color: "#FF9800", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {formatCurrency(statistics.bankTransferPayments)}
                </h3>
                <p style={styles.statLabel}>Bank Transfer</p>
              </div>
            </div>

            {/* Card 12: Dine In Orders */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FEF3F2",
                }}
              >
                <FontAwesomeIcon
                  icon={faUtensils}
                  style={{ color: "#FF6B35", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.dineInOrders}</h3>
                <p style={styles.statLabel}>Dine In Orders</p>
              </div>
            </div>

            {/* Card 13: Takeaway Orders */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#ECFDF5",
                }}
              >
                <FontAwesomeIcon
                  icon={faBagShopping}
                  style={{ color: "#10B981", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.takeawayOrders}</h3>
                <p style={styles.statLabel}>Takeaway Orders</p>
              </div>
            </div>

            {/* Card 14: Delivery Orders */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#EFF6FF",
                }}
              >
                <FontAwesomeIcon
                  icon={faMotorcycle}
                  style={{ color: "#3B82F6", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.deliveryOrders}</h3>
                <p style={styles.statLabel}>Delivery Orders</p>
              </div>
            </div>

            {/* Card 15: Empty spacer or additional stat if needed */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#F3F4F6",
                }}
              >
                <FontAwesomeIcon
                  icon={faCashRegister}
                  style={{ color: "#6B7280", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.totalOrders}</h3>
                <p style={styles.statLabel}>Total Orders</p>
              </div>
            </div>
          </div>

          {/* FILTERS BAR - With Refunded By Filter Always Visible */}
          <div style={styles.filtersBar}>
            <div style={styles.searchWrapper}>
              <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} />
              <input
                type="text"
                style={styles.searchInput}
                placeholder="Search by customer name or order #..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  style={styles.clearSearchButton}
                >
                  ✕
                </button>
              )}
            </div>

            {/* FROM DATE */}
            <div style={styles.dateWrapper}>
              <FontAwesomeIcon
                icon={faCalendarAlt}
                style={{ color: "#FF6B35" }}
              />
              <span style={styles.dateLabel}>From:</span>
              <input
                type="date"
                style={styles.dateInput}
                value={fromDate.toISOString().split("T")[0]}
                onChange={handleFromDateChange}
              />
            </div>

            {/* TO DATE */}
            <div style={styles.dateWrapper}>
              <FontAwesomeIcon
                icon={faCalendarAlt}
                style={{ color: "#FF6B35" }}
              />
              <span style={styles.dateLabel}>To:</span>
              <input
                type="date"
                style={styles.dateInput}
                value={toDate.toISOString().split("T")[0]}
                onChange={handleToDateChange}
              />
            </div>

            {/* Cashier Filter Dropdown */}
            <div style={styles.filterWrapper}>
              <select
                style={styles.filterSelect}
                value={selectedCashier}
                onChange={(e) => setSelectedCashier(e.target.value)}
              >
                <option value="all">All Cashiers</option>
                {cashierOptions.map((cashier) => (
                  <option key={cashier} value={cashier}>
                    {cashier}
                  </option>
                ))}
              </select>
            </div>

            {/* Refunded By Filter Dropdown - Always Visible */}
            <div style={styles.filterWrapper}>
              <select
                style={styles.filterSelect}
                value={selectedRefundedBy}
                onChange={(e) => setSelectedRefundedBy(e.target.value)}
              >
                <option value="all">All Refunded By</option>
                {refundedByOptions.length > 0 ? (
                  refundedByOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))
                ) : (
                  <option value="all" disabled>
                    No refunds found
                  </option>
                )}
              </select>
            </div>
          </div>

          {/* PAID ORDERS TABLE */}
          {filteredOrders.length === 0 ? (
            <div style={styles.emptyState}>
              <FontAwesomeIcon
                icon={faCashRegister}
                style={{ fontSize: "64px", color: "#CBD5E1" }}
              />
              <h3 style={styles.emptyTitle}>No Paid Orders Found</h3>
              <p style={styles.emptySubtitle}>
                {searchTerm
                  ? "No paid orders match your search criteria"
                  : `No paid orders found from ${formatDisplayDate(fromDate)} to ${formatDisplayDate(toDate)}`}
              </p>
            </div>
          ) : (
            <>
              <div style={styles.tableCard}>
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Order #</th>
                        <th style={styles.th}>Date</th>
                        <th style={styles.th}>Customer</th>
                        <th style={styles.th}>Type</th>
                        <th style={styles.th}>Table #</th>
                        <th style={styles.th}>Guests</th>
                        <th style={styles.th}>Items</th>
                        <th style={styles.th}>Original Total</th>
                        <th style={styles.th}>Discount</th>
                        <th style={styles.th}>Refunded</th>
                        <th style={styles.th}>Current Total</th>
                        <th style={styles.th}>Service Charge</th>
                        <th style={styles.th}>Payment Type</th>
                        <th style={styles.th}>Cashier</th>
                        <th style={styles.th}>Refunded By</th>
                        <th style={styles.th}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedOrders.map((order) => {
                        if (!order || !order.id) return null;

                        const orderType = getOrderTypeInfo(order.order_type);

                        // Calculate current values (after discounts and refunds)
                        const originalTotal =
                          order.original_total || order.total || 0;
                        const discount = order.discount || 0;
                        const subtotal = calculateCurrentSubtotal(order);
                        const serviceCharge =
                          calculateCurrentServiceCharge(order);
                        const currentTotal = subtotal + serviceCharge;

                        // Count active items (not fully refunded)
                        const totalItems =
                          order.items?.reduce((sum, item) => {
                            const refunded = item.refunded_quantity || 0;
                            return sum + (item.quantity - refunded);
                          }, 0) || 0;

                        const guestCount =
                          order.number_of_people ||
                          (order.order_type === "dine-in" ? "N/A" : "-");

                        // Calculate refund status
                        const isFullyRefunded =
                          order.refund_status === "full" ||
                          (order.total_refunded &&
                            order.total_refunded >= originalTotal);

                        // Calculate total refund amount (food + service charge)
                        const foodRefundAmount =
                          calculateFoodRefundAmount(order);
                        let serviceChargeRefund = 0;
                        if (order.order_type === "dine-in") {
                          const scPercentage =
                            order.service_charge_percentage || 5;
                          serviceChargeRefund =
                            foodRefundAmount * (scPercentage / 100);
                        }
                        const totalRefundAmount =
                          foodRefundAmount + serviceChargeRefund;

                        // Get cashier name
                        const cashierName =
                          order.cashier_name || order.cashier || "N/A";

                        // Get payment type info
                        const paymentType = getPaymentTypeInfo(
                          order.payment_method,
                        );

                        return (
                          <tr key={order.id} style={styles.tableRow}>
                            <td style={styles.td}>#{order.id}</td>
                            <td style={styles.td}>
                              {formatDisplayDate(order.created)}
                            </td>
                            <td style={styles.td}>
                              {order.guest_name || "Guest"}
                            </td>
                            <td style={styles.td}>
                              <span
                                style={{
                                  ...styles.orderTypeBadge,
                                  backgroundColor: orderType.bgColor,
                                  color: orderType.color,
                                }}
                              >
                                {orderType.label}
                              </span>
                            </td>
                            <td style={styles.td}>
                              {order.table_number || "-"}
                            </td>
                            <td style={styles.td}>
                              {order.order_type === "dine-in" ? (
                                <span>
                                  {guestCount}{" "}
                                  {guestCount === 1 ? "guest" : "guests"}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td style={styles.td}>
                              {totalItems} {totalItems === 1 ? "item" : "items"}
                            </td>
                            <td style={styles.td}>
                              <strong>{formatCurrency(originalTotal)}</strong>
                            </td>
                            <td style={styles.td}>
                              {discount > 0 ? (
                                <span
                                  style={{
                                    color: "#DC2626",
                                    fontWeight: "500",
                                  }}
                                >
                                  -{formatCurrency(discount)}
                                </span>
                              ) : (
                                <span style={{ color: "#9CA3AF" }}>-</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              {totalRefundAmount > 0 ? (
                                <span
                                  style={{
                                    color: "#DC2626",
                                    fontWeight: "500",
                                  }}
                                >
                                  -{formatCurrency(totalRefundAmount)}
                                </span>
                              ) : (
                                <span style={{ color: "#9CA3AF" }}>-</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              <strong style={{ color: "#4CAF50" }}>
                                {formatCurrency(currentTotal)}
                              </strong>
                            </td>
                            <td style={styles.td}>
                              {serviceCharge > 0
                                ? formatCurrency(serviceCharge)
                                : "-"}
                            </td>
                            <td style={styles.td}>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  color: paymentType.color,
                                  fontWeight: "500",
                                }}
                              >
                                {paymentType.label}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  color: "#3B82F6",
                                  fontWeight: "500",
                                }}
                              >
                                {cashierName}
                              </span>
                            </td>
                            <td style={styles.td}>
                              {order.refunded_by ? (
                                <span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  {order.refunded_by}
                                </span>
                              ) : (
                                <span style={{ color: "#9CA3AF" }}>-</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              <div style={styles.actionButtons}>
                                {!isFullyRefunded && (
                                  <button
                                    style={styles.refundButton}
                                    onClick={() => handleRefund(order)}
                                    title="Refund Order"
                                  >
                                    <FontAwesomeIcon icon={faMoneyBillWave} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {filteredTotalPages > 1 && (
                <div style={styles.paginationContainer}>
                  <button
                    style={{
                      ...styles.paginationButton,
                      opacity: canGoPrevious ? 1 : 0.5,
                    }}
                    onClick={goToPreviousPage}
                    disabled={!canGoPrevious}
                  >
                    Previous
                  </button>
                  <span style={styles.paginationInfo}>
                    Page {currentPage} of {filteredTotalPages} (
                    {filteredOrders.length} orders)
                  </span>
                  <button
                    style={{
                      ...styles.paginationButton,
                      opacity: canGoNext ? 1 : 0.5,
                    }}
                    onClick={goToNextPage}
                    disabled={!canGoNext}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <DashboardContentFooter />

      {/* RECEIPT MODAL */}
      {showReceiptModal && selectedReceipt && (
        <div className="modal">
          <div
            className="overlay"
            onClick={() => setShowReceiptModal(false)}
          ></div>
          <div className="modal-content" style={{ maxWidth: "500px" }}>
            <div className="modal-body">
              <div className="modal-header">
                <p>
                  <FontAwesomeIcon
                    icon={faFileInvoice}
                    style={{ marginRight: "10px", color: "#FF6B35" }}
                  />
                  Payment Receipt - Order #{selectedReceipt.id}
                </p>
                <button
                  className="close-modal"
                  onClick={() => setShowReceiptModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              {loadingReceipt ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    size="2x"
                    color="#FF6B35"
                  />
                  <p>Loading receipt...</p>
                </div>
              ) : (
                <div id="receipt-content" style={styles.receiptContainer}>
                  <div style={styles.receiptHeader}>
                    <h3 style={styles.receiptTitle}>THE HEADQUARTERS EATERY</h3>
                    <p style={styles.receiptSubtitle}>Payment Receipt</p>
                    <div style={styles.receiptDivider} />
                  </div>

                  <div style={styles.receiptInfo}>
                    <div style={styles.receiptRow}>
                      <span style={styles.receiptLabel}>Order #:</span>
                      <span style={styles.receiptValue}>
                        {selectedReceipt.id}
                      </span>
                    </div>
                    <div style={styles.receiptRow}>
                      <span style={styles.receiptLabel}>Date:</span>
                      <span style={styles.receiptValue}>
                        {formatDisplayDateTime(selectedReceipt.created)}
                      </span>
                    </div>
                    <div style={styles.receiptRow}>
                      <span style={styles.receiptLabel}>Customer:</span>
                      <span style={styles.receiptValue}>
                        {selectedReceipt.guest_name || "Guest"}
                      </span>
                    </div>
                    {selectedReceipt.table_number > 0 && (
                      <div style={styles.receiptRow}>
                        <span style={styles.receiptLabel}>Table:</span>
                        <span style={styles.receiptValue}>
                          {selectedReceipt.table_number}
                        </span>
                      </div>
                    )}
                    {selectedReceipt.number_of_people > 0 && (
                      <div style={styles.receiptRow}>
                        <span style={styles.receiptLabel}>Guests:</span>
                        <span style={styles.receiptValue}>
                          {selectedReceipt.number_of_people}
                        </span>
                      </div>
                    )}
                  </div>

                  <div style={styles.receiptDivider} />

                  <div style={styles.receiptItems}>
                    <div style={styles.receiptItemsHeader}>
                      <span>Item</span>
                      <span>Qty</span>
                      <span>Price</span>
                    </div>
                    {selectedReceipt.items?.map((item, idx) => {
                      const remainingQty =
                        item.quantity - (item.refunded_quantity || 0);
                      if (remainingQty <= 0) return null;
                      return (
                        <div key={idx} style={styles.receiptItemRow}>
                          <span style={styles.receiptItemName}>
                            {item.name}
                          </span>
                          <span style={styles.receiptItemQty}>
                            {remainingQty}
                          </span>
                          <span style={styles.receiptItemPrice}>
                            {formatCurrency(item.price * remainingQty)}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div style={styles.receiptDivider} />

                  <div style={styles.receiptTotals}>
                    <div style={styles.receiptRow}>
                      <span style={styles.receiptLabel}>Menu Subtotal:</span>
                      <span style={styles.receiptValue}>
                        {formatCurrency(
                          selectedReceipt.items?.reduce((sum, item) => {
                            const remainingQty =
                              item.quantity - (item.refunded_quantity || 0);
                            return sum + item.price * remainingQty;
                          }, 0) || 0,
                        )}
                      </span>
                    </div>
                    {selectedReceipt.discount > 0 && (
                      <div style={styles.receiptRow}>
                        <span style={styles.receiptLabel}>Discount:</span>
                        <span
                          style={{ ...styles.receiptValue, color: "#DC2626" }}
                        >
                          - {formatCurrency(selectedReceipt.discount)}
                        </span>
                      </div>
                    )}
                    {billDetails?.service_charge > 0 && (
                      <div style={styles.receiptRow}>
                        <span style={styles.receiptLabel}>
                          Service Charge (
                          {billDetails.service_charge_percentage}%):
                        </span>
                        <span
                          style={{ ...styles.receiptValue, color: "#E65100" }}
                        >
                          + {formatCurrency(billDetails.service_charge)}
                        </span>
                      </div>
                    )}
                    {selectedReceipt.service_charge_percentage === 0 && (
                      <div style={styles.receiptRow}>
                        <span style={styles.receiptLabel}>Service Charge:</span>
                        <span
                          style={{ ...styles.receiptValue, color: "#EF4444" }}
                        >
                          Waived
                        </span>
                      </div>
                    )}
                    <div
                      style={{
                        ...styles.receiptRow,
                        marginTop: "8px",
                        paddingTop: "8px",
                        borderTop: "2px solid #000",
                      }}
                    >
                      <span style={styles.receiptTotalLabel}>TOTAL PAID:</span>
                      <span style={styles.receiptTotalValue}>
                        {formatCurrency(selectedReceipt.total)}
                      </span>
                    </div>
                  </div>

                  <div style={styles.receiptFooter}>
                    <p>Thank you for dining with us!</p>
                    <p style={{ fontSize: "12px", color: "#666" }}>
                      Have a great day!
                    </p>
                    <div style={styles.receiptDivider} />
                    <p
                      style={{
                        fontSize: "10px",
                        color: "#999",
                        marginTop: "8px",
                      }}
                    >
                      VAT/TIN: P000123456789
                    </p>
                  </div>
                </div>
              )}

              <div style={styles.modalActions}>
                <button style={styles.printButton} onClick={handlePrintReceipt}>
                  <FontAwesomeIcon
                    icon={faPrint}
                    style={{ marginRight: "8px" }}
                  />
                  Print Receipt
                </button>
                <button
                  style={styles.closeButton}
                  onClick={() => setShowReceiptModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* UNIFIED REFUND MODAL */}
      {showRefundModal && selectedRefundOrder && selectedRefundOrder.id && (
        <UnifiedRefundModal
          order={selectedRefundOrder}
          onClose={() => {
            setShowRefundModal(false);
            setSelectedRefundOrder(null);
          }}
          onRefundSuccess={(result) => {
            setSuccessMessage(result.message);
            setModalSuccessMsg(true);
            mutate();
            setShowRefundModal(false);
            setSelectedRefundOrder(null);
          }}
        />
      )}

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message={successMessage}
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faClose}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f5f7fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "24px 32px 4rem 32px",
  },
  loadingContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  errorContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
    color: "#d32f2f",
  },
  retryButton: {
    marginTop: "20px",
    padding: "10px 20px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "500",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "16px",
    marginBottom: "20px",
  },
  statCard: {
    background: "white",
    padding: "16px",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  statIconContainer: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statValue: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "700",
    color: "#1F2937",
  },
  statLabel: {
    margin: "2px 0 0 0",
    color: "#6B7280",
    fontSize: "12px",
  },
  filtersBar: {
    display: "flex",
    gap: "12px",
    marginBottom: "20px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchWrapper: {
    flex: 2,
    position: "relative",
    minWidth: "200px",
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#9CA3AF",
  },
  searchInput: {
    width: "100%",
    padding: "14px 16px 14px 40px",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "white",
  },
  clearSearchButton: {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    color: "#666",
    cursor: "pointer",
    fontSize: "14px",
  },
  dateWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "10px 14px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  dateLabel: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#1F2937",
  },
  dateInput: {
    border: "none",
    fontSize: "14px",
    fontWeight: "500",
    color: "#1F2937",
    outline: "none",
    backgroundColor: "transparent",
    padding: "4px",
    minWidth: "120px",
  },
  filterWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    minWidth: "150px",
  },
  filterSelect: {
    padding: "14px 16px",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    backgroundColor: "white",
    fontSize: "14px",
    fontWeight: "500",
    color: "#1F2937",
    cursor: "pointer",
    outline: "none",
    minWidth: "140px",
    appearance: "auto",
  },
  tableCard: {
    background: "white",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1400px",
  },
  th: {
    textAlign: "left",
    padding: "14px 16px",
    backgroundColor: "#000000",
    color: "#FFFFFF",
    fontWeight: "600",
    fontSize: "13px",
  },
  td: {
    padding: "12px 16px",
    borderBottom: "1px solid #F0F0F0",
    fontSize: "14px",
    color: "#1F2937",
  },
  tableRow: {
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  orderTypeBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "500",
  },
  actionButtons: {
    display: "flex",
    gap: "6px",
    alignItems: "center",
  },
  refundButton: {
    backgroundColor: "#FEE2E2",
    color: "#DC2626",
    border: "none",
    borderRadius: "6px",
    padding: "6px 10px",
    fontSize: "12px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    transition: "background-color 0.2s",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "16px",
    marginTop: "20px",
    padding: "16px",
    backgroundColor: "white",
    borderRadius: "12px",
  },
  paginationButton: {
    padding: "12px 24px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  paginationInfo: {
    fontSize: "14px",
    color: "#6B7280",
  },
  emptyState: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1F2937",
    marginTop: "16px",
  },
  emptySubtitle: {
    fontSize: "14px",
    color: "#9CA3AF",
    marginTop: "8px",
  },
  receiptContainer: {
    padding: "20px",
    backgroundColor: "#FFFFFF",
    fontFamily: "'Courier New', monospace",
  },
  receiptHeader: {
    textAlign: "center",
    marginBottom: "20px",
  },
  receiptTitle: {
    fontSize: "16px",
    fontWeight: "bold",
    color: "#FF6B35",
    margin: 0,
  },
  receiptSubtitle: {
    fontSize: "12px",
    color: "#6B7280",
    margin: "4px 0",
  },
  receiptDivider: {
    height: "1px",
    backgroundColor: "#000",
    margin: "10px 0",
  },
  receiptInfo: {
    marginBottom: "16px",
  },
  receiptRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "6px",
    fontSize: "12px",
  },
  receiptLabel: {
    color: "#64748B",
  },
  receiptValue: {
    color: "#1E293B",
    fontWeight: "500",
  },
  receiptItems: {
    marginBottom: "16px",
  },
  receiptItemsHeader: {
    display: "flex",
    justifyContent: "space-between",
    fontWeight: "bold",
    fontSize: "11px",
    paddingBottom: "6px",
    borderBottom: "1px dashed #000",
    marginBottom: "8px",
  },
  receiptItemRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "11px",
    marginBottom: "4px",
  },
  receiptItemName: {
    flex: 2,
    color: "#1E293B",
  },
  receiptItemQty: {
    width: "40px",
    textAlign: "center",
    color: "#64748B",
  },
  receiptItemPrice: {
    width: "70px",
    textAlign: "right",
    color: "#1E293B",
  },
  receiptTotals: {
    marginBottom: "16px",
  },
  receiptTotalLabel: {
    fontSize: "15px",
    fontWeight: "bold",
    color: "#1F2937",
  },
  receiptTotalValue: {
    fontSize: "16px",
    fontWeight: "bold",
    color: "#FF6B35",
  },
  receiptFooter: {
    textAlign: "center",
    marginTop: "16px",
  },
  modalActions: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: "1px solid #E2E8F0",
  },
  printButton: {
    flex: 1,
    padding: "10px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  closeButton: {
    flex: 1,
    padding: "10px",
    backgroundColor: "#64748B",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
};
